import { useCallback, useEffect, useState } from 'react';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import { useUpdateCaptableCustomBreakpoints } from 'services/hooks/captable';
import useWaterfallBreakpointsTableData from './useWaterfallBreakpointsTableData';
import { CustomBreakpointsData, WaterfallBreakpointsProps, WaterfallSaveState } from '../../types/breakpoint-analysis.models';

export type { WaterfallSaveState };

const WaterfallBreakpoints = ({
  waterfallColumns,
  format,
  hideGrid,
  initialLoadWithCustomBPs,
  isDisabled,
  isAllocationFinal,
  capTableId,
  customBreakpointsGridRef,
  onSaveStateChange,
}: WaterfallBreakpointsProps) => {
  const { spreadsheet: WaterfallBreakpointsDetailSheet } = useWaterfallBreakpointsTableData({
    waterfallColumns,
    format,
  });

  const [spreadsheets, setSpreadsheets] = useState([WaterfallBreakpointsDetailSheet]);
  const { updateCustomBreakpoints } = useUpdateCaptableCustomBreakpoints();

  useEffect(() => {
    if (WaterfallBreakpointsDetailSheet?.yLength > 0) {
      setSpreadsheets([WaterfallBreakpointsDetailSheet]);
    }
  }, [WaterfallBreakpointsDetailSheet]);

  const { onChange, workbook } = useWorkbook(spreadsheets);

  const WaterfallBreakpointsDetailSheetProps = {
    ...WaterfallBreakpointsDetailSheet,
    sheet: WaterfallBreakpointsDetailSheet!,
    workbook,
    onChange,
    page: 'captable',
    className: 'waterfall-custom-width',
    data: WaterfallBreakpointsDetailSheet?.data ?? [],
  };

  const handleSaveWaterfallBreakpoints = useCallback(async () => {
    await updateCustomBreakpoints(capTableId, {
      ...customBreakpointsGridRef.current,
      use_custom_breakpoints: false,
    } as CustomBreakpointsData);
    // change flag so the save button goes away after saving
    // eslint-disable-next-line no-param-reassign
    initialLoadWithCustomBPs.current = false;
    onSaveStateChange?.(null);
  }, [capTableId, updateCustomBreakpoints, initialLoadWithCustomBPs, customBreakpointsGridRef, onSaveStateChange]);

  // Notify parent of save state: show save button only when switching back from custom BPs to waterfall.
  useEffect(() => {
    if (initialLoadWithCustomBPs.current) {
      onSaveStateChange?.({
        save: handleSaveWaterfallBreakpoints,
        disabled: isDisabled,
        locked: isAllocationFinal,
      });
    } else {
      onSaveStateChange?.(null);
    }
  }, [handleSaveWaterfallBreakpoints, initialLoadWithCustomBPs, isAllocationFinal, isDisabled, onSaveStateChange]);

  if (hideGrid) {
    return null;
  }

  return WaterfallBreakpointsDetailSheet?.data ? (
    <ScalarSpreadsheet {...WaterfallBreakpointsDetailSheetProps} page={'captable'} />
  ) : (
    <GridSkeleton />
  );
};

export default WaterfallBreakpoints;
